import { createContext, useContext, useState, useEffect } from 'react';
import { organizationsAPI, usersAPI } from '../api/client';
import { getCurrentUser } from 'aws-amplify/auth';

const OrgContext = createContext(null);

export function OrgProvider({ children }) {
  const [currentOrg, setCurrentOrg] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Load user's organizations on mount
  useEffect(() => {
    loadUserOrganizations();
  }, []);

  // Load organizations from localStorage on mount
  useEffect(() => {
    const savedOrgId = localStorage.getItem('currentOrgId');
    if (savedOrgId && organizations.length > 0) {
      const org = organizations.find(o => o.org_id === savedOrgId);
      if (org) {
        setCurrentOrg(org);
      } else {
        // If saved org not found, set first org
        setCurrentOrg(organizations[0]);
      }
    } else if (organizations.length > 0) {
      // No saved org, set first org
      setCurrentOrg(organizations[0]);
    }
  }, [organizations]);

  // Save current org to localStorage whenever it changes
  useEffect(() => {
    if (currentOrg) {
      localStorage.setItem('currentOrgId', currentOrg.org_id);
    }
  }, [currentOrg]);

  async function loadUserOrganizations() {
    try {
      setLoading(true);
      const response = await organizationsAPI.getUserOrganizations();

      setOrganizations(response.data.assignments || []);
      setIsSuperAdmin(response.data.is_super_admin || false);

      // Fetch user profile
      try {
        const cognitoUser = await getCurrentUser();
        const userResponse = await usersAPI.getById(cognitoUser.userId);
        setUserProfile(userResponse.data.user);
      } catch (err) {
        console.error('Failed to load user profile:', err);
      }
    } catch (error) {
      console.error('Failed to load user organizations:', error);
    } finally {
      setLoading(false);
    }
  }

  function switchOrganization(org) {
    setCurrentOrg(org);
  }

  const value = {
    currentOrg,
    organizations,
    isSuperAdmin,
    loading,
    userProfile,
    switchOrganization,
    refreshOrganizations: loadUserOrganizations
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
}
